import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import {
  Button, Empty, Loading, Row,
} from '../../components/Ui';
import { useToast } from '../../context/ToastContext';
import { currency, dateTime } from '../../utils/format';
import { INSPECTION_PARTS, SERVICE_STAGES } from '../../constants';

const CONDITION = {
  ok: { label: 'OK', color: 'var(--success)' },
  attention: { label: 'Needs attention', color: 'var(--warning)' },
  replace: { label: 'Replace', color: 'var(--danger)' },
};
const partLabel = (p) => (INSPECTION_PARTS.find((x) => x.value === p) || {}).label || p;

/** Live tracker for one service job: stages, inspection, estimate and bill. */
const ServiceJobPage = () => {
  const { id } = useParams();
  const toast = useToast();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.garage.job(id)
      .then((r) => setJob(r.data))
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  const respond = async (approve) => {
    setBusy(true);
    try {
      const res = await api.garage.respondToEstimate(id, { approve, reason: approve ? '' : 'Not needed right now' });
      setJob(res.data);
      toast.success(res.message, approve ? 'Approved' : 'Refused');
    } catch (err) {
      toast.error(err.message, 'Could not send');
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Cancel this service? The garage will be told.')) return;
    try {
      const res = await api.garage.cancel(id, { reason: 'Changed my mind' });
      setJob(res.data);
    } catch (err) { toast.error(err.message, 'Could not cancel'); }
  };

  if (loading) return <Loading text="Loading your service…" />;
  if (!job) return <Empty emoji="🔧" title="Job not found" />;

  const doneStages = job.timeline.map((t) => t.status);
  const stages = SERVICE_STAGES.filter((s) => (
    job.mode === 'pickup_drop' || !['pickup_assigned', 'picked_up', 'out_for_delivery'].includes(s.value)
  ));

  return (
    <div style={{ maxWidth: 860 }}>
      <PageHeader
        title={`${job.vehicleInfo && job.vehicleInfo.brand} ${job.vehicleInfo && job.vehicleInfo.model}`}
        subtitle={`${job.jobNo} · ${job.status.replace(/_/g, ' ')}`}
        back
      />

      {job.estimate && job.estimate.status === 'pending' ? (
        <div className="card sh-md" style={{ borderColor: 'var(--warning)', marginBottom: 16 }}>
          <div className="t-title">Extra work needs your approval</div>
          {job.estimate.items.map((i, x) => (
            <Row key={`${i.label}-${x}`} label={`${i.label} (${i.kind})`} value={currency(i.amount)} />
          ))}
          <Row label="Additional work" value={currency(job.estimate.total)} />
          {job.estimate.note ? <div className="t-tiny">💬 {job.estimate.note}</div> : null}
          <div className="row" style={{ gap: 12, marginTop: 12 }}>
            <Button title="✅ Approve" loading={busy} onClick={() => respond(true)} />
            <Button title="❌ Refuse" variant="light" onClick={() => respond(false)} />
          </div>
          <div className="t-tiny" style={{ marginTop: 8 }}>Nothing extra is done to your vehicle until you approve.</div>
        </div>
      ) : null}

      <div className="split-2" style={{ gap: 16, alignItems: 'start' }}>
        <div className="card sh-sm">
          <div className="t-title">Progress</div>
          {stages.map((s) => {
            const done = doneStages.includes(s.value);
            const at = [...job.timeline].reverse().find((t) => t.status === s.value);
            return (
              <div className="row" key={s.value} style={{ gap: 10, alignItems: 'flex-start', padding: '5px 0' }}>
                <span>{done ? '✅' : '⚪️'}</span>
                <span>
                  <span className="t-body" style={{ display: 'block', opacity: done ? 1 : 0.5 }}>{s.label}</span>
                  {at ? <span className="t-tiny">{dateTime(at.at)}{at.note ? ` · ${at.note}` : ''}</span> : null}
                </span>
              </div>
            );
          })}
          {job.status === 'cancelled' ? (
            <div style={{ color: 'var(--danger)', fontWeight: 700 }}>Cancelled · {job.cancelReason}</div>
          ) : null}
        </div>

        <div className="card sh-sm">
          <div className="t-title">Booking</div>
          <Row label="Services" value={job.services.map((s) => s.title).join(', ')} />
          <Row label="How" value={job.mode.replace(/_/g, ' ')} />
          {job.scheduledDate ? <Row label="When" value={`${new Date(job.scheduledDate).toDateString()} · ${job.scheduledSlot}`} /> : null}
          <Row label="Odometer" value={`${job.vehicleInfo && job.vehicleInfo.km} km`} />
          {job.partner ? (
            <>
              <Row label="Garage" value={job.partner.businessName} />
              <a className="link-btn" href={`tel:${job.partner.phone}`}>📞 Call the garage</a>
            </>
          ) : null}
        </div>
      </div>

      {job.problem && (job.problem.text || (job.problem.photos || []).length || job.problem.video) ? (
        <div className="card sh-sm" style={{ marginTop: 16 }}>
          <div className="t-title">What you reported</div>
          {job.problem.text ? <p className="t-small">{job.problem.text}</p> : null}
          <div className="row wrap" style={{ gap: 8 }}>
            {(job.problem.photos || []).map((p) => (
              <img key={p} src={fileUrl(p)} alt="Problem" style={{ width: 110, height: 110, borderRadius: 10, objectFit: 'cover' }} />
            ))}
          </div>
          {job.problem.video ? <video src={fileUrl(job.problem.video)} controls style={{ width: '100%', maxHeight: 200, borderRadius: 12, background: '#000' }} /> : null}
        </div>
      ) : null}

      {(job.inspection || []).length ? (
        <div className="card sh-sm" style={{ marginTop: 16 }}>
          <div className="t-title">Inspection report</div>
          <div className="t-tiny">Checked {dateTime(job.inspectedAt)}</div>
          {job.inspection.map((i, x) => (
            <div className="row-between row-line" key={`${i.part}-${x}`}>
              <span>
                <span className="t-body" style={{ display: 'block' }}>{partLabel(i.part)}</span>
                {i.note ? <span className="t-tiny">{i.note}</span> : null}
                <span className="row wrap" style={{ gap: 6 }}>
                  {(i.photos || []).map((p) => (
                    <img key={p} src={fileUrl(p)} alt="" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover' }} />
                  ))}
                </span>
              </span>
              <span style={{ color: (CONDITION[i.condition] || {}).color, fontWeight: 700, fontSize: 12 }}>
                {(CONDITION[i.condition] || {}).label}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {job.bill && job.bill.total ? (
        <div className="card sh-sm" style={{ marginTop: 16 }}>
          <div className="t-title">Bill{job.invoiceNo ? ` · ${job.invoiceNo}` : ''}</div>
          <Row label="Service charges" value={currency(job.bill.serviceCharges)} />
          {(job.partsUsed || []).map((p, x) => (
            <Row key={`${p.name}-${x}`} label={`${p.name} × ${p.qty}`} value={currency(p.amount)} />
          ))}
          <Row label="Labour" value={currency(job.bill.labour)} />
          {job.bill.discount ? <Row label="Discount" value={`− ${currency(job.bill.discount)}`} /> : null}
          <Row label="Taxes" value={currency(job.bill.taxes)} />
          <Row label="Total" value={currency(job.bill.total)} />
          <div className="t-tiny">{job.paymentMode.toUpperCase()} · {job.paymentStatus}</div>
        </div>
      ) : null}

      {['requested', 'accepted', 'pickup_assigned'].includes(job.status) ? (
        <div style={{ marginTop: 16 }}>
          <Button title="Cancel this service" variant="light" onClick={cancel} />
        </div>
      ) : null}
    </div>
  );
};

export default ServiceJobPage;
